import { formatDate as format, formatName } from '../lib/format';
import { useEffect } from "react";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { getQuarterHoursFromTimes } from "../common";
import { resolvePdfGraphic } from "./pdfMedia";
import { runPdf } from "../utils/pdfSettle";
pdfMake.vfs = pdfFonts['Roboto-Regular.ttf'];

const workerCapacityAbbreviations = {
    "Traffic Controller": "TC",
    "General Labourer": "GL",
    "Skilled Labourer": "SL",
    "Skilled Worker A": "SW-A",
    "Skilled Worker B": "SW-B",
};

const AllocationPdfGenerator = ({ allocation, timesheets, workers, clients, sites, assets, logo, onComplete, onBuffer, onError }) => {
    useEffect(() => {
        if (allocation) {
            generatePdf().catch(error => onError?.(error, allocation));
        }
    }, [allocation, timesheets]);

    const parseBreakMinutes = (value) => {
        if (value === null || value === undefined || value === "") return 0;
        if (typeof value === "number" && Number.isFinite(value)) return value;
        const match = String(value).match(/(\d+(\.\d+)?)/);
        return match ? Number(match[1]) : 0;
    };

    const formatDateTime = (value) => {
        if (!value) return "-";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return "-";
        return format(date, "dd MMM yyyy h:mm a");
    };

    const formatDateOnly = (value) => {
        if (!value) return "-";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return "-";
        return format(date, "dd MMM yyyy");
    };

    const calculateTotalHours = (start, end, breakValue, breakIncludedInPay = true) => {
        if (!start || !end) return "-";
        const startDate = new Date(start);
        const endDate = new Date(end);
        if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return "-";
        const hours = getQuarterHoursFromTimes({
            clockIn: startDate,
            clockOut: endDate,
            breakMinutes: parseBreakMinutes(breakValue),
            breakIncludedInPay,
        });
        return hours === "" ? "-" : hours.toFixed(2);
    };

    const buildLocationCell = (coords) => {
        if (!Array.isArray(coords) || coords.length < 2) return { text: "-" };
        const [lat, lng] = coords;
        if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return { text: "-" };
        const label = `${Number(lat).toFixed(6)}, ${Number(lng).toFixed(6)}`;
        return {
            text: label,
            link: `https://www.google.com/maps?q=${lat},${lng}`,
            color: "#1d4ed8",
            decoration: "underline",
        };
    };

    const getCapacityAbbreviation = (capacity) => {
        const normalizedCapacity = String(capacity || "").trim();
        return workerCapacityAbbreviations[normalizedCapacity] || "";
    };

    const generatePdf = async () => {
        const logoGraphic = await resolvePdfGraphic(logo);
        const clientName = clients?.find(r => r.id === allocation?.chosenClient)?.name || "-";
        const site = sites?.find(r => r.id === allocation?.chosenSite);
        const hasMapMarker = !!allocation?.dropMapMarker
            || !!String(allocation?.mapMarkerAddress || allocation?.mapMarkerName || '').trim()
            || (Array.isArray(allocation?.mapMarkerCoordinates) && allocation.mapMarkerCoordinates.length >= 2)
        const siteName = site?.name || "-";
        const siteAddress = hasMapMarker
            ? (String(allocation?.mapMarkerAddress || '').trim() || String(allocation?.mapMarkerName || '').trim() || "-")
            : (site?.address || "-");

        const workerLookup = (workers || []).reduce((acc, worker) => {
            acc[worker.id] = formatName(`${worker.firstName} ${worker.lastName}`);
            return acc;
        }, {});
        const workerCapacityLookup = (allocation?.chosenWorkerAssignments || []).reduce((acc, assignment) => {
            acc[assignment?.workerId] = assignment?.capacity || "";
            return acc;
        }, {});
        const workerDefaultCapacityLookup = (workers || []).reduce((acc, worker) => {
            acc[worker.id] = Array.isArray(worker?.workerType) ? (worker.workerType[0] || "") : "";
            return acc;
        }, {});

        const timesheetLookup = (timesheets || []).reduce((acc, row) => {
            acc[row.userId] = row;
            return acc;
        }, {});

        const chosenWorkerIds = allocation?.chosenWorkers || [];
        const rows = chosenWorkerIds.length
            ? chosenWorkerIds.map(workerId => {
                const row = timesheetLookup[workerId] || {};
                const capacity = workerCapacityLookup[workerId] || row.workerCapacity || workerDefaultCapacityLookup[workerId] || "";
                return [
                    workerLookup[workerId] || "-",
                    getCapacityAbbreviation(capacity),
                    formatDateTime(row.clockIn),
                    formatDateTime(row.clockOut),
                    `${parseBreakMinutes(row.breakMinutes)} mins`,
                    calculateTotalHours(row.clockIn, row.clockOut, row.breakMinutes, row.breakIncludedInPay),
                    buildLocationCell(row.locationCoordinates),
                ];
            })
            : (timesheets && timesheets.length
                ? timesheets.map(row => [
                    workerLookup[row.userId] || "-",
                    getCapacityAbbreviation(workerCapacityLookup[row.userId] || row.workerCapacity || workerDefaultCapacityLookup[row.userId] || ""),
                    formatDateTime(row.clockIn),
                    formatDateTime(row.clockOut),
                    `${parseBreakMinutes(row.breakMinutes)} mins`,
                    calculateTotalHours(row.clockIn, row.clockOut, row.breakMinutes, row.breakIncludedInPay),
                    buildLocationCell(row.locationCoordinates),
                ])
                : [["No timesheets recorded", "", "-", "-", "-", "-", "-"]]);

        const workerOrder = [
            ...(allocation?.teamLeaders || []),
            ...chosenWorkerIds.filter(id => !(allocation?.teamLeaders || []).includes(id)),
        ];
        const candidateIds = workerOrder.length ? workerOrder : (timesheets || []).map(row => row.userId);
        const supervisorGroups = new Map();
        candidateIds.forEach(workerId => {
            const row = timesheetLookup[workerId] || {};
            if (!row.supervisorName && !row.supervisorSign && !row.supervisorNotAvailable) return;
            const key = JSON.stringify([row.supervisorName || '', row.supervisorSign || '', !!row.supervisorNotAvailable]);
            if (!supervisorGroups.has(key)) supervisorGroups.set(key, { row, names: [] });
            supervisorGroups.get(key).names.push(workerLookup[workerId] || workerId);
        });
        const supervisorRows = await Promise.all([...supervisorGroups.values()].map(async ({ row, names }) => {
            const graphic = row.supervisorSign ? await resolvePdfGraphic(row.supervisorSign) : null;
            return [row.supervisorName || '-', names.join(', '), row.supervisorNotAvailable ? { text: 'Supervisor not present on site to sign', italics: true } : graphic ? { ...graphic, fit: [120, 40] } : { text: '' }];
        }));

        const assetsRows = allocation?.chosenAssets && allocation.chosenAssets.length
            ? allocation.chosenAssets.map(entry => {
                const asset = assets?.find(r => r.id === entry.asset);
                return [
                    asset?.machineId + " " + asset?.regoNo,
                    // asset?.make || "-",
                    // asset?.model || "-",
                ];
            })
            : [["No assets recorded"]];

        const docDefinition = {
            content: [
                {
                    columns: [
                        logoGraphic
                            ? { ...logoGraphic, fit: [120, 60], margin: [0, 0, 0, 10] }
                            : { text: " " },
                        {
                            stack: [
                                { text: "ALLOCATION TIMESHEET", style: "title", alignment: "right" },
                                {
                                    text: [
                                        { text: "Shift Date: ", bold: true },
                                        `${formatDateOnly(allocation?.date)}\n`,
                                        { text: "Generated: ", bold: true },
                                        `${formatDateTime(new Date())}\n`,
                                        { text: "Allocation ID: ", bold: true },
                                        `${allocation.allocationId}\n`,
                                    ],
                                    alignment: "right",
                                    margin: [0, 5, 0, 0],
                                },
                            ],
                        },
                    ],
                },
                { text: " " },
                {
                    columns: [
                        [
                            { text: "Client", bold: true, margin: [0, 0, 0, 2] },
                            { text: clientName, },
                            // { text: "Client" },
                        ],
                        [
                            { text: "Site Details", bold: true, margin: [0, 0, 0, 2] },
                            { text: siteName },
                            { text: siteAddress },
                        ],
                    ],
                },
                { text: "\n" },
                { text: "Shift Details", style: "sectionTitle" },
                {
                    table: {
                        widths: ["*", "auto", "*", "*", "auto", "auto", "*"],
                        body: [
                            [
                                { text: "Worker", style: "tableHeader" },
                                { text: "Capacity", style: "tableHeader" },
                                { text: "Clock In", style: "tableHeader" },
                                { text: "Clock Out", style: "tableHeader" },
                                { text: "Break", style: "tableHeader" },
                                { text: "Total Hours", style: "tableHeader" },
                                { text: "Location", style: "tableHeader" },
                            ],
                            ...rows,
                        ],
                    },
                    layout: "lightHorizontalLines",
                    margin: [0, 8, 0, 16],
                },
                { text: "Assets Used", style: "sectionTitle" },
                {
                    table: {
                        widths: ["*"],
                        body: [
                            [
                                { text: "Asset ID", style: "tableHeader" },
                                // { text: "Make", style: "tableHeader" },
                                // { text: "Model", style: "tableHeader" },
                            ],
                            ...assetsRows,
                        ],
                    },
                    layout: "lightHorizontalLines",
                    margin: [0, 8, 0, 16],
                },
                { text: "Supervisor Sign Off", style: "sectionTitle" },
                {
                    table: {
                        widths: ["*", "*", "*"],
                        body: [
                            [{ text: 'Supervisor Name', style: 'tableHeader' }, { text: 'Workers', style: 'tableHeader' }, { text: 'Signature', style: 'tableHeader' }],
                            ...(supervisorRows.length ? supervisorRows : [['-', '-', '']]),
                        ],
                    },
                    layout: "lightHorizontalLines",
                },
            ],
            styles: {
                title: {
                    fontSize: 18,
                    bold: true,
                },
                sectionTitle: {
                    fontSize: 12,
                    bold: true,
                },
                tableHeader: {
                    fillColor: "#f2f2f2",
                    bold: true,
                },
            },
            defaultStyle: {
                fontSize: 10,
            },
        };

        const fileLabel = allocation?.date ? formatDateOnly(allocation.date).replace(/\//g, "-") : "allocation";
        const pdf = pdfMake.createPdf(docDefinition);
        // Failures from either path reject generatePdf and reach onError, which every
        // caller passes; before, a build failure left the combined download queue and
        // the "Downloading allocation..." popup waiting forever.
        if (onBuffer) {
            const buffer = await runPdf(done => pdf.getBuffer(done));
            await onBuffer(buffer, allocation);
        } else {
            await runPdf(done => pdf.download(`allocation_${fileLabel}_${allocation.allocationId}.pdf`, done));
            if (onComplete) onComplete();
        }
    };

    return null;
};

export default AllocationPdfGenerator;
